import { useRef, useState } from 'preact/hooks';
import { backupAll, backupFilename, restoreFrom } from '../store/backup';
import { useApp } from './app-context';
import { downloadText, formatBytes } from './files';

export function BackupPanel({ onRestored }: { onRestored?: () => void }) {
  const { repo, device } = useApp();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const backup = async () => {
    setBusy(true);
    try {
      const text = await backupAll(repo, device);
      const name = backupFilename();
      downloadText(text, name);
      await repo.setSetting('lastBackupAt', Date.now());
      setMsg({ kind: 'ok', text: `Saved ${name} (${formatBytes(text.length)})` });
    } catch (e) {
      setMsg({ kind: 'err', text: `Backup failed: ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  };

  const restore = async (file: File) => {
    setBusy(true);
    try {
      const r = await restoreFrom(repo, await file.text());
      setMsg({ kind: 'ok', text: `Restored ${file.name}: ${r.added} added, ${r.updated} updated, ${r.skipped} already up to date` });
      onRestored?.();
    } catch (e) {
      setMsg({ kind: 'err', text: `Restore failed: ${(e as Error).message}` });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div class="backup">
      <div class="row">
        <button class="btn primary" disabled={busy} onClick={backup}>Back up everything</button>
        <button class="btn" disabled={busy} onClick={() => fileRef.current?.click()}>Restore from file…</button>
        <input
          ref={fileRef} type="file" accept=".json,application/json" hidden
          onChange={(e) => {
            const f = (e.currentTarget as HTMLInputElement).files?.[0];
            if (f) void restore(f);
          }}
        />
      </div>
      <p class="hint">Restore merges by id: nothing on this device is deleted, and a record is only replaced by a newer version.</p>
      {msg && <p class={msg.kind === 'ok' ? 'note ok' : 'note err'}>{msg.text}</p>}
    </div>
  );
}
